import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api, API_BASE } from '../../utils/api';

// Normalize various timestamp formats to milliseconds
const normalizeTs = (ts) => {
    if (ts == null) return null;
    if (typeof ts === 'object') {
        if (ts.$numberLong) return Number(ts.$numberLong);
        if (ts.$date) return Number(ts.$date);
        return null;
    }
    if (typeof ts === 'number') return ts < 1e12 ? ts * 1000 : ts;
    if (typeof ts === 'string') {
        if (/^\d+$/.test(ts)) {
            const n = Number(ts);
            return n < 1e12 ? n * 1000 : n;
        }
        const parsed = Date.parse(ts);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
};

const parseResponse = (raw) => {
    if (raw == null) return {};
    try {
        if (typeof raw === 'string') {
            const s = raw.trim();
            if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
                try { return JSON.parse(s); } catch (e) { return { raw: s }; }
            }
            return { raw: s };
        }
        if (typeof raw === 'object') return raw;
        return { raw: String(raw) };
    } catch (e) { return { raw: String(raw) }; }
};

// Fetch logs (all or filtered by driver)
const fetchLogs = async (paramsObj = {}) => {
    const params = new URLSearchParams();
    params.set('limit', '10000');
    // set params only when not null/undefined
    Object.entries(paramsObj || {}).forEach(([k, v]) => { if (v !== undefined && v !== null) params.set(k, String(v)); });
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/ridematch/logs?${params.toString()}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    });
    if (!res.ok) throw new Error('Failed to fetch ridematch logs');
    return res.json();
};

const RideMatch = () => {
    const [selected, setSelected] = useState(null); // { driverId, column }
    // date picker (single date only) and search
    const todayYMD = (() => {
        const d = new Date();
        const yy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yy}-${mm}-${dd}`;
    })();
    const [dateSelected, setDateSelected] = useState(todayYMD);
    const [searchTerm, setSearchTerm] = useState('');
    const [developerFilter, setDeveloperFilter] = useState('Exclude');
    const [page, setPage] = useState(1);
    const limit = 10;

    // Fetch all drivers to filter those with ridematch logs
    const { data: driversRes, isLoading: loadingDrivers } = useQuery(['drivers', 'all', developerFilter], async () => {
        const res = await api.get('/drivers', { params: { limit: 10000, developerOnly: developerFilter } });
        return res?.data?.data?.drivers || res?.data?.drivers || [];
    }, { staleTime: 5 * 60 * 1000 });

    const allDrivers = useMemo(() => {
        return (driversRes || []).map(d => {
            const id = d._id && typeof d._id === 'object' && d._id.$oid ? d._id.$oid : (d._id || d.id);
            return { ...d, id: String(id) };
        });
    }, [driversRes]);

    // Fetch logs for ALL drivers to determine which have ridematch logs
    const { data: logsRes, isLoading: loadingLogs } = useQuery(
        ['ridematch-logs', dateSelected],
        async () => {
            if (!dateSelected) return [];
            return fetchLogs({ statsDate: dateSelected });
        },
        { staleTime: 60 * 1000, keepPreviousData: true }
    );

    const logs = useMemo(() => Array.isArray(logsRes) ? logsRes : (logsRes?.data?.logs || logsRes?.logs || []), [logsRes]);

    const driverStats = useMemo(() => {
        const map = {};
        const ensure = (id) => { if (!map[id]) map[id] = { sent: 0, requestAttempts: 0, tripRequestAttempt: 0, accepted: 0, timeouts: 0, rejected: 0, notconnected: 0, trips: [] }; };
        for (const l of logs) {
            const drv = String(l.driverId || l.driver || 'unknown');
            ensure(drv);
            const parsed = parseResponse(l.response);
            const evtRaw = l.event || parsed.event || parsed.type || parsed.action || '';
            const evt = String(evtRaw || '').toLowerCase();
            const resp = ((l.response || parsed.response) || '').toString().toLowerCase();

            // Count request attempts (aliases) and exact trip_request_attempt separately
            if (evt === 'trip_request_attempt') {
                map[drv].tripRequestAttempt += 1;
                map[drv].requestAttempts += 1;
            } else if (evt === 'socket_message_sent' || evt === 'request_sent') {
                map[drv].requestAttempts += 1;
            }

            if (evt === 'driver_trip_response') {
                if (resp === 'accept') map[drv].accepted += 1;
                else if (resp === 'reject') map[drv].rejected += 1;
                else if (resp === 'timeout') map[drv].timeouts += 1;
            }
            if (evt === 'driver_not_connected') map[drv].notconnected += 1;

            // capture trip-level info for modal
            map[drv].trips.push({
                tripId: l.tripId || l.trip || (parsed.trip_id || parsed.request_id) || 'unknown',
                event: String(evtRaw),
                response: l.response || parsed.response || parsed.raw || '',
                ts: normalizeTs(l.timestamp?.$numberLong || l.timestamp || l.createdAt)
            });
        }

        return map;
    }, [logs]);

    // Filter to only drivers that have ridematch logs (non-zero counts or trips)
    const driversWithLogs = useMemo(() => {
        return allDrivers.filter(d => {
            const s = driverStats[d.id];
            if (!s) return false;
            const total = (s.tripRequestAttempt || 0) + (s.accepted || 0) + (s.timeouts || 0) + (s.rejected || 0) + (s.notconnected || 0);
            if (total > 0) return true;
            if (s.trips && s.trips.length > 0) return true;
            return false;
        });
    }, [allDrivers, driverStats]);

    // Apply search (by name or driver id)
    const driversWithLogsFiltered = useMemo(() => {
        const q = (searchTerm || '').trim().toLowerCase();
        if (!q) return driversWithLogs;
        return driversWithLogs.filter(d => {
            const name = (d.name || '').toString().toLowerCase();
            const id = (d.id || '').toString().toLowerCase();
            const license = (d.licenseNo || '').toString().toLowerCase();
            return name.includes(q) || id.includes(q) || license.includes(q);
        });
    }, [driversWithLogs, searchTerm]);

    // Paginate filtered drivers (10 per page)
    const paginatedDrivers = useMemo(() => {
        const start = (page - 1) * limit;
        const end = start + limit;
        return driversWithLogsFiltered.slice(start, end);
    }, [driversWithLogsFiltered, page, limit]);

    const totalPages = Math.ceil(driversWithLogsFiltered.length / limit);

    const openModal = (driverId, column) => {
        setSelected({ driverId, column });
        setModalPage(1); // Reset to first page
    };
    // Modal trip details state (similar to Drivers page requests modal)
    const [requestTrips, setRequestTrips] = useState([]);
    const [isLoadingRequests, setIsLoadingRequests] = useState(false);
    const [requestsDriver, setRequestsDriver] = useState(null);
    const [modalPage, setModalPage] = useState(1);
    const [modalPagination, setModalPagination] = useState({ current: 1, total: 1, totalRecords: 0, limit: 10 });

    const getColumnDisplayName = (column) => {
        const names = {
            'sent': 'Request Attempts',
            'accepted': 'Accepted',
            'rejected': 'Rejected',
            'timeouts': 'Timeouts',
            'notconnected': 'Socket Not Connected'
        };
        return names[column] || column;
    };

    const fetchTripsForDriverColumn = async (driverId, column, page = 1) => {
        setIsLoadingRequests(true);
        setRequestTrips([]);
        try {
            const params = { statsDate: dateSelected, limit: 10, page, column };
            const res = await api.get(`/drivers/${driverId}/requests`, { params });
            const trips = res?.data?.data?.trips || res?.data?.trips || res?.data || [];
            const pagination = res?.data?.data?.pagination || res?.data?.pagination || { current: page, total: Math.ceil((trips.length || 0) / 10) || 1, totalRecords: trips.length || 0, limit: 10 };
            setModalPagination(pagination);

            // Enrich each trip with the actual response status from log data
            const stats = driverStats[driverId];
            if (stats && stats.trips) {
                trips.forEach(trip => {
                    const tripId = trip._id || trip.id || trip.tripId;
                    const tripLogs = stats.trips.filter(t => t.tripId === tripId);

                    let actualStatus = column;
                    if (column === 'sent') {
                        for (const log of tripLogs) {
                            const evt = String(log.event || '').toLowerCase();
                            const resp = String(log.response || '').toLowerCase();

                            if (evt === 'driver_trip_response') {
                                if (resp === 'accept') {
                                    actualStatus = 'accepted';
                                    break;
                                } else if (resp === 'reject') {
                                    actualStatus = 'rejected';
                                    break;
                                } else if (resp === 'timeout') {
                                    actualStatus = 'timeouts';
                                    break;
                                }
                            } else if (evt === 'driver_not_connected') {
                                actualStatus = 'notconnected';
                            }
                        }
                    }
                    trip._logStatus = actualStatus;
                });
            }

            trips.sort((a, b) => new Date(b.createdOn || b.createdAt) - new Date(a.createdOn || a.createdAt));
            setRequestTrips(trips);
        } catch (e) {
            console.error('Error fetching trips for column:', e);
            setRequestTrips([]);
            setModalPagination({ current: 1, total: 1, totalRecords: 0, limit: 10 });
        } finally {
            setIsLoadingRequests(false);
        }
    };

    const isBusy = loadingDrivers || loadingLogs;

    return (
        <div className="space-y-6">
            <div className="page-header">
                <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
                    <div>
                        <h1 className="page-title">Drivers — RideMatch Overview</h1>
                        <p className="page-subtitle">Driver status and aggregated request metrics</p>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    {isBusy ? (
                        <div className="py-8 text-center">Loading...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-4">
                                    <input placeholder="Search name or driverId" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }} className="border px-3 py-2 rounded w-64" />
                                    <div className="flex items-center space-x-2">
                                        <label className="text-sm text-gray-700">Developer Filter:</label>
                                        <select
                                            value={developerFilter}
                                            onChange={(e) => { setDeveloperFilter(e.target.value); setPage(1); }}
                                            className="border px-3 py-2 rounded bg-white"
                                        >
                                            <option value="Exclude">Exclude Developers</option>
                                            <option value="Include">Include Developers</option>
                                            <option value="Only">Developers Only</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <label className="text-sm text-gray-700">Date:</label>
                                    <input type="date" value={dateSelected} onChange={e => { setDateSelected(e.target.value); setPage(1); }} className="border px-2 py-1 rounded" />
                                </div>
                            </div>
                            <table className="table">
                                <thead className="table-header">
                                    <tr className="text-left bg-gray-50">
                                        <th className="table-cell">Driver</th>
                                        <th className="table-cell">Request Attempts</th>
                                        <th className="table-cell">Accepted</th>
                                        <th className="table-cell">Timeouts</th>
                                        <th className="table-cell">Rejected</th>
                                        <th className="table-cell">Socket Not Connected</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedDrivers.map(d => {
                                        const stats = driverStats[d.id] || { sent: 0, accepted: 0, timeouts: 0, rejected: 0, notconnected: 0, trips: [] };
                                        return (
                                            <tr key={d.id} className="table-row">
                                                <td className="table-cell">
                                                    <div className="flex items-center">
                                                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                                            <span className="text-sm font-medium text-primary-700">{d.name?.charAt(0)?.toUpperCase()}</span>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">{d.name || d.id}</div>
                                                            <div className="text-sm text-gray-500">{d.licenseNo || ''}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="table-cell"><button className="text-blue-600 bg-blue-50 border rounded-xl px-7 py-2" onClick={() => { openModal(d.id, 'sent'); fetchTripsForDriverColumn(d.id, 'sent'); setRequestsDriver(d); }}>{stats.tripRequestAttempt ?? stats.requestAttempts ?? stats.sent}</button></td>
                                                <td className="table-cell"><button className="text-green-600 bg-green-50 border rounded-xl px-7 py-2" onClick={() => { openModal(d.id, 'accepted'); fetchTripsForDriverColumn(d.id, 'accepted'); setRequestsDriver(d); }}>{stats.accepted}</button></td>
                                                <td className="table-cell"><button className="text-yellow-600 bg-yellow-50 border rounded-xl px-7 py-2" onClick={() => { openModal(d.id, 'timeouts'); fetchTripsForDriverColumn(d.id, 'timeouts'); setRequestsDriver(d); }}>{stats.timeouts}</button></td>
                                                <td className="table-cell"><button className="text-red-600 bg-red-50 border rounded-xl px-7 py-2" onClick={() => { openModal(d.id, 'rejected'); fetchTripsForDriverColumn(d.id, 'rejected'); setRequestsDriver(d); }}>{stats.rejected}</button></td>
                                                <td className="table-cell"><button className="text-gray-700 bg-gray-50 border rounded-xl px-7 py-2" onClick={() => { openModal(d.id, 'notconnected'); fetchTripsForDriverColumn(d.id, 'notconnected'); setRequestsDriver(d); }}>{stats.notconnected}</button></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {/* Pagination controls */}
                            {driversWithLogsFiltered.length > 0 && (
                                <div className="flex items-center justify-between mt-4 px-4">
                                    <div className="text-sm text-gray-700">
                                        Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, driversWithLogsFiltered.length)} of {driversWithLogsFiltered.length} drivers with ridematch logs
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page <= 1}
                                            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>
                                        <span className="px-3 py-1">
                                            Page {page} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setPage(p => p + 1)}
                                            disabled={page >= totalPages}
                                            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for trips */}
            {selected && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 max-w-7xl w-full max-h-[90vh] flex flex-col relative">
                        <button onClick={() => { setSelected(null); setRequestTrips([]); setRequestsDriver(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 font-bold text-lg">✕</button>

                        <div className="flex-shrink-0 mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">{requestsDriver?.name || 'Driver'} — {getColumnDisplayName(selected?.column)}</h3>
                        </div>

                        <div className="overflow-x-auto overflow-y-auto max-h-[60vh] flex-1 border rounded-lg">
                            <table className="table w-full border-collapse">
                                <thead className="table-header sticky top-0 bg-gray-50 z-10">
                                    <tr>
                                        <th className="table-header-cell">Trip ID</th>
                                        <th className="table-header-cell">Passenger</th>
                                        <th className="table-header-cell">Accepted Driver</th>
                                        <th className="table-header-cell">Log Status</th>
                                        <th className="table-header-cell">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="table-body">
                                    {isLoadingRequests ? (
                                        <tr><td colSpan="5" className="py-6 text-center">Loading...</td></tr>
                                    ) : requestTrips.length === 0 ? (
                                        <tr><td colSpan="5" className="py-6 text-center">No trips found</td></tr>
                                    ) : (
                                        requestTrips.map((trip) => {
                                            const bookingForName = trip?.userId?.name || trip?.bookingForName || trip?.userName || trip?.passengerName || '--';
                                            const driverName = requestsDriver?.name || requestsDriver?.phone || 'Driver';

                                            // Determine the status to display
                                            const displayStatus = trip._logStatus || selected.column;

                                            // Color coding for status badges
                                            const statusColors = {
                                                'accepted': 'bg-green-100 text-green-800',
                                                'rejected': 'bg-red-100 text-red-800',
                                                'timeouts': 'bg-yellow-100 text-yellow-800',
                                                'notconnected': 'bg-gray-200 text-gray-800',
                                                'sent': 'bg-blue-100 text-blue-800'
                                            };

                                            return (
                                                <tr key={trip._id || trip.id || trip.tripId} className="table-row">
                                                    <td className="table-cell"><Link to={`/trips/${trip._id || trip.id || trip.tripId}`} className="text-primary-600 hover:underline font-medium">{trip._id || trip.id || trip.tripId}</Link></td>
                                                    <td className="table-cell text-sm text-gray-900">{bookingForName}</td>
                                                    <td className="table-cell text-sm text-gray-900">{driverName}</td>
                                                    <td className="table-cell">
                                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[displayStatus] || 'bg-gray-100 text-gray-800'}`}>
                                                            {getColumnDisplayName(displayStatus)}
                                                        </span>
                                                    </td>
                                                    <td className="table-cell"><Link to={`/trips/${trip._id || trip.id || trip.tripId}`} className="text-primary-600 hover:text-primary-900 font-medium">View</Link></td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination controls */}
                        {modalPagination.totalRecords > 0 && modalPagination.total > 1 && (
                            <div className="mt-4 flex flex-shrink-0 items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing {((modalPagination.current - 1) * modalPagination.limit) + 1} to{' '}
                                    {Math.min(modalPagination.current * modalPagination.limit, modalPagination.totalRecords)} of{' '}
                                    {modalPagination.totalRecords} trips
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => {
                                            const newPage = modalPagination.current - 1;
                                            setModalPage(newPage);
                                            fetchTripsForDriverColumn(selected.driverId, selected.column, newPage);
                                        }}
                                        disabled={modalPagination.current === 1 || isLoadingRequests}
                                        className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm">
                                        Page {modalPagination.current} of {modalPagination.total}
                                    </span>
                                    <button
                                        onClick={() => {
                                            const newPage = modalPagination.current + 1;
                                            setModalPage(newPage);
                                            fetchTripsForDriverColumn(selected.driverId, selected.column, newPage);
                                        }}
                                        disabled={modalPagination.current >= modalPagination.total || isLoadingRequests}
                                        className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex flex-shrink-0 justify-end">
                            <button onClick={() => { setSelected(null); setRequestTrips([]); setRequestsDriver(null); setModalPage(1); setModalPagination({ current: 1, total: 1, totalRecords: 0, limit: 10 }); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RideMatch;